import React, { useEffect, useMemo, useRef, useState } from 'react'
import { RhyBusInputText, RhyBusCustomSelect, RhyBusTextArea } from '@rhybus/form-controls'
import { RhyBusFileUpload } from '@rhybus/file-upload'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { RhyBusCard } from '@rhybus/card'
import { RhyBusButton } from '@rhybus/button'
import { RhyBusAlert } from '@rhybus/alert'
import Instructions from '../../utils/controls/instructions/instructions'
import PageOverlay from '../../utils/controls/pageoverLay/pageoverLay'
import { RhyBusValidationSummary } from '@rhybus/validation-summary'
import { UPDATE_ALERT, FISCAL_OPTIONS } from '../../config/configuration'
import { useForm } from '../../services/form'
import { uploadFilesToCaseBlobs, getFilesForDetail, softDeleteBlobFiles, getDetailFileContent } from '../../services/detailview'
import { useFormMetadataOptions } from '../../services/metadataManagement'
import { RhyBusToolTip } from '@rhybus/tooltip'
import { useTooltipResolver } from '../../services/use-tooltip-resolver'
import {
  TT_GRANT_CASE_TYPE, TT_GRANT_PROGRAM_TYPE, TT_GRANT_FUNDING_SOURCE, TT_GRANT_IA,
  TT_GRANT_COUNTRY, TT_GRANT_START_FY, TT_GRANT_END_FY, TT_GRANT_CASE_DESIGNATOR,
  TT_GRANT_USER_CASE_ID, TT_GRANT_LINE_NUMBER, TT_GRANT_REGIONAL_CENTER,
  TT_GRANT_DESCRIPTION, TT_GRANT_ATTACHMENTS,
} from '../../services/tooltip-ids'
const Form: React.FC = () => {
    const { itemGuid } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const copyState = (location.state as any)?.isCopied ? (location.state as any) : null
    const { formRef, values, handleChange, handleBlur, handleSave, handleCancel, handleSubmit, handleUpdate, showCancelAlert, setShowCancelAlert, showInstructions, setShowInstructions, validationMsgs, setValidationMsgs, focusField, position, loadFormByGuid, loadGrantLinesByGuid, prepareForCopy, hasEditValueChanges, currentStatusCode, currentCaseGUID, formHydrationVersion, endFiscalYearOptions } = useForm('grant') as any
    // const [publishedSchema, setPublishedSchema] = useState(() => loadPublishedSchema('grant'))
    // useEffect(() => {
    //     // Load from SQL Server first (source of truth for all users)
    //     loadActiveSchema('grant').then(serverSchema => {
    //         if (serverSchema) {
    //             setPublishedSchema(serverSchema as any)
    //             return
    //         }
    //         // Fallback: restore from IndexedDB if server unavailable
    //         syncPublishedSchemaFromIDB('grant').then(restored => {
    //             if (restored) setPublishedSchema(loadPublishedSchema('grant'))
    //         })
    //     })
    // }, [])
    const resolveTooltip = useTooltipResolver()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const isSubmittingRef = useRef(false)
    const [loading, setLoading] = useState(!!(copyState || itemGuid))
    const [showNoChangesAlert, setShowNoChangesAlert] = useState(false)
    const [showSuccessAlert, setShowSuccessAlert] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [pendingNavPath, setPendingNavPath] = useState<string | null>(null)
    const [pendingTabKey, setPendingTabKey] = useState<string | null>(null)
    const fileMapRef = useRef<Record<string, File[]>>({})
    const uploadFileCacheRef = useRef<Map<string, File>>(new Map())
    const initialAttachmentCountRef = useRef<number>(0)
    const attachmentsRef = useRef<any[]>([])
    const removedAttachmentsRef = useRef<any[]>([])
    const [fileUploadError, setFileUploadError] = useState<string | null>(null)
    const [grantLineInputError, setGrantLineInputError] = useState<string | null>(null)
    const [grantLineResetKey, setGrantLineResetKey] = useState(0)
    const [inlineEditError, setInlineEditError] = useState<string | null>(null)
    type GrantLineItem = { lineNumber: string; regionalCenter: string; eKey?: string; eTag?: string; caseMasterLineGUID?: string }
    const [grantLines, setGrantLines] = useState<GrantLineItem[]>([])
    const [originalGrantLines, setOriginalGrantLines] = useState<GrantLineItem[]>([])
    const [grantLineInput, setGrantLineInput] = useState({ lineNumber: '', regionalCenter: '' })
    const [editingGrantLineIndex, setEditingGrantLineIndex] = useState<number | null>(null)
    const [inlineEditValues, setInlineEditValues] = useState<GrantLineItem>({ lineNumber: '', regionalCenter: '' })

    const { isLoading: metadataLoading, countryOptions, implementingAgencyOptions, programTypeOptions, fundingSourceOptions, programTypeFundingSourceMap, programTypeCodeMap, regionalCenterItems } = useFormMetadataOptions()

    const handleProgramTypeChange = (val: any) => {
        handleChange('programType', val)
        const programTypeValue = typeof val === 'object' ? (val?.value ?? '') : (val ?? '')
        const autoFundingSource = programTypeFundingSourceMap[programTypeValue]
        if (autoFundingSource) {
            handleChange('fundingSource', { label: autoFundingSource, value: autoFundingSource })
        } else {
            handleChange('fundingSource', null)
        }
    }

    const selectedProgramTypeValue = typeof values.programType === 'object'
        ? (values.programType?.value ?? '')
        : (values.programType ?? '')
    const isFundingSourceLocked = !!programTypeFundingSourceMap[selectedProgramTypeValue]

    const regionalCenterOptions = useMemo(() => {
        const ptCode = programTypeCodeMap[selectedProgramTypeValue]
        if (ptCode == null || regionalCenterItems.length === 0) return []
        return regionalCenterItems
            .filter(rc => rc.ParentCode != null && Number(rc.ParentCode) === Number(ptCode))
            .map(rc => {
                const val = rc.MetadataValue ?? ''
                const desc = rc.MetadataDescription ? ` - ${rc.MetadataDescription}` : ''
                return { label: `${val}${desc}`, value: val }
            })
            .filter(o => o.value !== '')
    }, [programTypeCodeMap, regionalCenterItems, selectedProgramTypeValue])

    const handleGrantLineAdd = () => {
        if (!grantLineInput.lineNumber.trim()) {
            setGrantLineInputError('Line Number is required')
            return
        }
        const isDuplicate = grantLines.some((l: any) => l.lineNumber === grantLineInput.lineNumber.trim())
        if (isDuplicate) {
            setGrantLineInputError('A case line with this line number already exists.')
            return
        }
        setGrantLineInputError(null)
        setGrantLines(prev => [...prev, { lineNumber: grantLineInput.lineNumber.trim(), regionalCenter: grantLineInput.regionalCenter }])
        setGrantLineInput({ lineNumber: '', regionalCenter: '' })
        setGrantLineResetKey(k => k + 1)
    }

    const handleGrantLineEdit = (index: number) => {
        setInlineEditValues({ ...grantLines[index] })
        setEditingGrantLineIndex(index)
    }

    const handleGrantLineSave = (index: number) => {
        if (!inlineEditValues.lineNumber.trim()) {
            setInlineEditError('Line Number is required')
            return
        }
        setInlineEditError(null)
        setGrantLines(prev => prev.map((l, i) => i === index ? { ...inlineEditValues, lineNumber: inlineEditValues.lineNumber.trim() } : l))
        setEditingGrantLineIndex(null)
        setInlineEditValues({ lineNumber: '', regionalCenter: '' })
    }

    const handleGrantLineCancelEdit = () => {
        setInlineEditError(null)
        setEditingGrantLineIndex(null)
        setInlineEditValues({ lineNumber: '', regionalCenter: '' })
    }

    const handleGrantLineDelete = (index: number) => {
        setGrantLines(prev => prev.filter((_, i) => i !== index))
        if (editingGrantLineIndex === index) {
            setEditingGrantLineIndex(null)
            setInlineEditValues({ lineNumber: '', regionalCenter: '' })
        }
    }

    const optionsMap = useMemo(() => ({
        'Fiscal Year': FISCAL_OPTIONS,
        'Country': countryOptions,
        'Implementing Agency': implementingAgencyOptions,
        'Funding Source': fundingSourceOptions,
        'Program Type': programTypeOptions,
    }), [countryOptions, implementingAgencyOptions, programTypeOptions, fundingSourceOptions])

    useEffect(() => {
        if (!itemGuid) return
        loadFormByGuid(itemGuid).then(({ caseGUID, itemKey }: { caseGUID: string | null; itemKey?: string | null }) => {
            if (!caseGUID) { setLoading(false); return }
            const linesPromise = loadGrantLinesByGuid(caseGUID).then((lines: { lineNumber: string; regionalCenter: string }[]) => {
                setGrantLines(lines)
                setOriginalGrantLines(lines)
            })
            const detailFileKey = itemKey || itemGuid
            getFilesForDetail(detailFileKey).then((fileStructure: any) => {
                const allFiles = fileStructure?.allFilesList ?? []
                // Only prefill files that were uploaded from the Grant Form attachment section
                const filteredFiles = allFiles.filter((f: any) => f.metadata?.blobContainerName === 'grant-form')
                const preloaded = filteredFiles.map((f: any) => ({ ...f, name: f.Name || f.metadata?.name || '' }))
                console.log({
                    itemKey: detailFileKey,
                    blobContainerName: 'grant-form',
                    allFiles: allFiles.length,
                    filteredFiles: filteredFiles.length,
                    prefilledFiles: preloaded.map((f: any) => f.name),
                })
                initialAttachmentCountRef.current = preloaded.length
                attachmentsRef.current = preloaded
                if (preloaded.length > 0) handleChange('attachments', preloaded)
            })
            return linesPromise
        }).finally(() => setLoading(false))
    }, [itemGuid, loadFormByGuid, loadGrantLinesByGuid])

    useEffect(() => {
        if (!copyState) return
        const sourceCaseGUID = copyState.copiedData?.CaseGUID ?? copyState.copiedData?.ItemGUID ?? copyState.copiedData?.eKey
        if (!sourceCaseGUID) { setLoading(false); return }
        loadFormByGuid(sourceCaseGUID).then(({ caseGUID, itemKey }: { caseGUID: string | null; itemKey?: string | null }) => {
            if (!caseGUID) { setLoading(false); return }
            const linesPromise = loadGrantLinesByGuid(caseGUID).then((lines: { lineNumber: string; regionalCenter: string }[]) => {
                // Strip source identifiers so lines are treated as new during submit
                setGrantLines(lines.map((l: any) => ({ lineNumber: l.lineNumber, regionalCenter: l.regionalCenter })))
                setOriginalGrantLines([])
            })
            const sourceItemKey = itemKey || sourceCaseGUID
            getFilesForDetail(sourceItemKey).then((fileStructure: any) => {
                const allFiles = fileStructure?.allFilesList ?? []
                const filteredFiles = allFiles.filter((f: any) => f.metadata?.blobContainerName === 'grant-form')
                const preloaded = filteredFiles.map((f: any) => ({ ...f, name: f.Name || f.metadata?.name || '' }))
                attachmentsRef.current = preloaded
                preloaded.forEach(async (f: any) => {
                    try {
                        const content = await getDetailFileContent(sourceItemKey, { Name: f.Name, folderName: f.folderName })
                        if (content.byteLength > 0) {
                            uploadFileCacheRef.current.set(`${f.name}_${f.size}`, new File([content], f.name, { type: f.metadata?.type || '' }))
                        }
                    } catch { /* non-fatal: file simply won't be re-uploaded */ }
                })
                if (preloaded.length > 0) handleChange('attachments', preloaded)
            })
            // Reset eKey/eTag/CaseGUID so submit creates a new record instead of updating the source
            prepareForCopy()
            return linesPromise
        }).finally(() => setLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Capture dragged files (drop events don't fire a change event, so captureActualFiles misses them)
    useEffect(() => {
        const captureDrop = (e: DragEvent) => {
            const files = e.dataTransfer?.files
            if (!files?.length) return
            Array.from(files).forEach((f) => uploadFileCacheRef.current.set(`${f.name}_${f.size}`, f))
        }
        document.addEventListener('drop', captureDrop, true)
        return () => document.removeEventListener('drop', captureDrop, true)
    }, [])

    const captureActualFiles = (fieldKey: string, e: any) => {
        const target = e.target as HTMLInputElement
        if (target?.type === 'file' && target.files) {
            const files = Array.from(target.files)
            fileMapRef.current[fieldKey] = files
            files.forEach((f) => uploadFileCacheRef.current.set(`${f.name}_${f.size}`, f))
        }
    }

    const onFileChange = (fieldKey: string, strippedFiles: any[]) => {
        const actualFiles = fileMapRef.current[fieldKey] ?? []

        const merged = strippedFiles
            .map((stripped: any) => {
                if (!stripped || stripped instanceof File) return stripped

                return actualFiles.find(
                    (f: File) =>
                        f.name === (stripped.name || stripped.FileName) &&
                        f.size === stripped.size
                ) ?? stripped
            })
            .filter(Boolean)

        const oversized = merged.find(
            (f: any) => f?.size > 25 * 1024 * 1024
        )
        if (oversized) {
            const message = `${oversized.name} - File exceeds 25 MB`
            setFileUploadError(message)
            setValidationMsgs([
                {
                    key: fieldKey,
                    text: message,
                },
            ])
            fileMapRef.current[fieldKey] = []
            uploadFileCacheRef.current.clear()
            return
        }
        setFileUploadError(null)

        if (fieldKey === 'attachments') {

            const previous =
                attachmentsRef.current || []

            removedAttachmentsRef.current =
                previous.filter(
                    (p: any) =>
                        !merged.some(
                            (m: any) =>
                                (m.Name || m.name) === (p.Name || p.name)
                        )
                )

            attachmentsRef.current = merged
        }

        fileMapRef.current[fieldKey] = []
        handleChange(fieldKey, merged)
    }

    const isActuallyEditMode = !!itemGuid
    const isSavedDraft = isActuallyEditMode && currentStatusCode === 1
    const formTitle = isActuallyEditMode ? 'Edit Case' : 'Initialize Case'
    // isSubmitted: true only when a status is known AND it is not a draft (statusCode !== 1)
    const isSubmitted = currentStatusCode !== null && Number(currentStatusCode) !== 1
    // buttonsReady: new form is always ready; existing form waits until status is loaded to avoid flicker
    const buttonsReady = !itemGuid || currentStatusCode !== null
    const showSaveAsDraft = !isSubmitted
    const cancelNavPath = isSubmitted ? `/detail-view/${currentCaseGUID}` : '/cases'
    const primaryActionLabel = 'Save as Draft'
    const primaryActionIcon = 'icon-rb-saved color-primary'
    const submitActionLabel = isSubmitted ? 'Update' : 'Submit'
    const submitActionIcon = isSubmitted ? 'icon-rb-update' : 'icon-rb-checked'
    const newLines = grantLines.filter(l => !l.eKey)
    const modifiedLines = grantLines.filter(l =>
        l.eKey && originalGrantLines.some(o =>
            o.eKey === l.eKey && (o.regionalCenter !== l.regionalCenter || o.lineNumber !== l.lineNumber)
        )
    )
    const deletedLines = originalGrantLines.filter(
        o => o.eKey && !grantLines.some(l => l.eKey === o.eKey)
    )
    const hasGrantLineChanges = newLines.length > 0 || modifiedLines.length > 0 || deletedLines.length > 0
    const handleCancelClick = () => {
        if (hasEditValueChanges() || hasGrantLineChanges || uploadFileCacheRef.current.size > 0 || removedAttachmentsRef.current.length > 0) {
            handleCancel()
        } else {
            navigate(cancelNavPath)
        }
    }
    const onUpdate = async (position: 'top' | 'bottom') => {
        const initialAttachments = initialAttachmentCountRef.current
        const currentAttachments = values.attachments?.length ?? 0

        const hasAttachmentChanges =
            !fileUploadError &&
            (
                uploadFileCacheRef.current.size > 0 ||
                removedAttachmentsRef.current.length > 0
            )
        const hasFormChanges = hasEditValueChanges()
        if (
            !hasFormChanges &&
            !hasGrantLineChanges &&
            !hasAttachmentChanges
        ) {
            setShowNoChangesAlert(true)
            return
        }
        return handleUpdate(position, grantLines, originalGrantLines)
    }

    const showSuccess = (message: string, navPath: string | null, tabkey?: string) => {
        setSuccessMessage(message)
        setPendingNavPath(navPath)
        setPendingTabKey(tabkey ?? null)
        setShowSuccessAlert(true)
    }

    const uploadAttachmentsAfterSubmit = async (response: any) => {
        const blobItemKey = response?.itemKey ?? response?.response?.eKey ?? response?.eKey ?? (isActuallyEditMode ? itemGuid : null)
        const caseGUID = response?.caseGUID ?? currentCaseGUID ?? null
        const formType = 'case'
        const pageName = 'grant-form'
        const blobContainerName = 'grant-form'
        // Resolve actual File instances: use value directly if it's a File, otherwise look up by name+size in cache
        // The cache is populated by captureActualFiles (click) and the drop listener (drag-and-drop)

        const filesToUpload = (values.attachments ?? [])
            .map((f: any) => {
                if (f instanceof File) return f
                return uploadFileCacheRef.current.get(`${f.name}_${f.size}`) ?? null
            })
            .filter((f: File | null): f is File => f !== null)
        console.log({
            formType,
            caseGUID,
            itemKey: blobItemKey,
            attachments: filesToUpload.map((f: File) => f.name),
            pageName,
            blobContainerName,
            uploadStarted: filesToUpload.length > 0 && !!blobItemKey,
            uploadCompleted: false,
            uploadError: null,
        })
        if ((filesToUpload.length > 0 || removedAttachmentsRef.current.length > 0) && blobItemKey) {
            if (
                removedAttachmentsRef.current.length > 0 &&
                blobItemKey
            ) {
                await softDeleteBlobFiles(
                    blobItemKey,
                    removedAttachmentsRef.current
                )

                removedAttachmentsRef.current = []
            }
            try {
                const uploadResult = await uploadFilesToCaseBlobs(blobItemKey, filesToUpload, undefined, 'grant-form')
                console.log({
                    formType,
                    caseGUID,
                    itemKey: blobItemKey,
                    attachments: filesToUpload.map((f: File) => f.name),
                    pageName,
                    blobContainerName,
                    uploadStarted: true,
                    uploadCompleted: uploadResult,
                    uploadError: uploadResult ? null : 'One or more files failed to upload',
                })
                filesToUpload.forEach((f: File) => uploadFileCacheRef.current.delete(`${f.name}_${f.size}`))
            } catch (err) {
                console.error('[uploadAttachmentsAfterSubmit]', err)
                console.log({
                    formType,
                    caseGUID,
                    itemKey: blobItemKey,
                    attachments: filesToUpload.map((f: File) => f.name),
                    pageName,
                    blobContainerName,
                    uploadStarted: true,
                    uploadCompleted: false,
                    uploadError: String(err),
                })
            }
        }
    }

    const handleSubmitAction = async (pos: 'top' | 'bottom') => {
        if (isSubmittingRef.current) return
        isSubmittingRef.current = true
        setIsSubmitting(true)
        try {
            if (isActuallyEditMode && !isSavedDraft) {
                const response = await onUpdate(pos)
                if (response !== false && response !== undefined) {
                    await uploadAttachmentsAfterSubmit(response)
                    showSuccess('Case updated successfully.', `/detail-view/${currentCaseGUID}`)
                }
            } else {
                const response = await handleSubmit(pos, grantLines, originalGrantLines)
                if (response !== false && response !== undefined) {
                    await uploadAttachmentsAfterSubmit(response)
                    const itemKey = response?.itemKey
                    const caseGUID = response?.caseGUID
                    console.log({ formData: 'submitted', attachedFiles: (values.attachments ?? []).length, itemKey, submitCompleted: true })
                    showSuccess('Case submitted successfully.', caseGUID ? `/detail-view/${caseGUID}` : '/cases')
                }
            }
        } catch {
            // error surfaced by inner handlers
        } finally {
            isSubmittingRef.current = false
            setIsSubmitting(false)
        }
    }


    const handleSaveAction = async (pos: 'top' | 'bottom') => {
        if (isSubmittingRef.current) return
        if (isActuallyEditMode && !isSavedDraft) {
            if (!hasEditValueChanges() && !hasGrantLineChanges && !(uploadFileCacheRef.current.size > 0) && !(removedAttachmentsRef.current.length > 0)) {
                setShowNoChangesAlert(true)
                return
            }
        } else {
            if (!hasEditValueChanges() && !hasGrantLineChanges && !(uploadFileCacheRef.current.size > 0) && !(removedAttachmentsRef.current.length > 0)) {
                setShowNoChangesAlert(true)
                return
            }
        }
        isSubmittingRef.current = true
        setIsSubmitting(true)
        try {
            if (isActuallyEditMode && !isSavedDraft) {
                const response = await onUpdate(pos)
                if (response !== false && response !== undefined) {
                    showSuccess('Case updated successfully.', `/detail-view/${currentCaseGUID}`)
                }
            } else {
                const response = await handleSave(pos, grantLines, originalGrantLines)
                if (response !== false && response !== undefined) {
                    await uploadAttachmentsAfterSubmit(response)
                    showSuccess('Case saved as draft successfully.', '/cases', 'saved')
                }
            }
        } catch {
            // error surfaced by inner handlers
        } finally {
            isSubmittingRef.current = false
            setIsSubmitting(false)
        }
    }
    return (
        <>
            {(loading || metadataLoading || isSubmitting) && <PageOverlay />}
            <RhyBusAlert
                show={showCancelAlert}
                size="md"
                variant="confirm"
                message={'Are you sure you want to cancel? Unsaved changes will be lost.'}
                confirmLabel="Yes"
                cancelLabel="No"
                okIcon="icon-rb-checked"
                cancelIcon="icon-rb-close"
                onConfirm={() => {
                    setShowCancelAlert(false);
                    navigate(cancelNavPath)
                }}
                onCancel={() => setShowCancelAlert(false)}
            />
            <RhyBusAlert
                size='sm'
                show={showNoChangesAlert}
                variant="confirm"
                message={UPDATE_ALERT}
                confirmLabel="OK"
                onConfirm={() => setShowNoChangesAlert(false)}
            />
            <RhyBusAlert
                show={showSuccessAlert}
                size="md"
                variant="success"
                title="Success"
                message={successMessage}
                autoClose={3000}
                onClose={() => {
                    setShowSuccessAlert(false)
                    if (pendingNavPath) navigate(pendingNavPath, pendingTabKey ? { state: { tab: pendingTabKey } } : undefined)
                }}
            />
            <main id="main-content">
                <div className="container">
                    <div className="row">
                        <div className="col-12">
                            <RhyBusCard
                                cardSize='lg'
                                isTitleBorder={true}
                                cardHeaderLeft={
                                    <div className="d-flex align-items-center gap-2">
                                        <h2 className='inter-semibold font-18' tabIndex={0} aria-label={formTitle}><span className="icon-rb-create-form color-primary font-14 me-1" aria-hidden="true"></span>{formTitle}</h2>
                                        <RhyBusButton style={{ '--rb-btn-bg': '#2D3A91', fontWeight: '400' }} variant="primary" label="Instructions" className="font-12 inter-medium" rightIcon={showInstructions ? 'icon-rb-arrow-up font-12' : 'icon-rb-arrow-down font-12'} size="sm" onClick={() => setShowInstructions((s: boolean) => !s)} />
                                    </div>
                                }
                                cardHeaderRight={
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <RhyBusButton
                                            variant="secondary"
                                            label="Cancel"
                                            leftIcon="icon-rb-close"
                                            onClick={handleCancelClick}
                                        />
                                        {buttonsReady && showSaveAsDraft && (
                                            <RhyBusButton
                                                variant="secondary"
                                                label={primaryActionLabel}
                                                leftIcon="icon-rb-saved color-primary"
                                                onClick={() => handleSaveAction('top')}
                                                style={{ fontWeight: '600' }}
                                            />
                                        )}
                                        {buttonsReady && (
                                            <RhyBusButton
                                                variant="primary"
                                                label={submitActionLabel}
                                                leftIcon={submitActionIcon}
                                                onClick={() => handleSubmitAction('top')}
                                                style={{ fontWeight: '600' }}
                                            />
                                        )}
                                    </div>
                                }
                                cardbodyclassName='pt-2'
                                cardBody={
                                    <>
                                        {showInstructions && <Instructions />}
                                        {validationMsgs && validationMsgs.length > 0 && position === 'top' && (

                                            <RhyBusValidationSummary
                                                errors={validationMsgs.map((m: any) => ({ name: m.key, label: m.text }))}
                                                variant="info"
                                                showClose={true}
                                                onClose={() => setValidationMsgs([])}
                                                onErrorClick={(error: any) => focusField(error.name)}
                                            />
                                        )}
                                        {/* FORM_ENGINE_START */}
                                        {/* {publishedSchema ? (
                                        <DynamicFormRenderer schema={publishedSchema} values={values} handleChange={handleChangeWithCalc} formRef={formRef} formHydrationVersion={formHydrationVersion} optionsMap={optionsMap} />
                                    ) : ( */}
                                        <div id="cr-container-main" className="row form-md darktext">
                                            <div className="col-12 mt-0">
                                                <div id="cr-row-group-0-fields-1" className="row p-relative form-md darktext">
                                                    <div className="col-12 col-md-6 col-lg-3" data-rb-select="caseType">
                                                        <RhyBusCustomSelect id="select-caseType" name="Case Type" label="Case Type" placeholder="Select" isMandatory={true} info={resolveTooltip(TT_GRANT_CASE_TYPE)} options={[{ label: 'Grants', value: 'Grants' }]} value={values.caseType} onChange={(v: any) => handleChange('caseType', v)} />
                                                    </div>
                                                    <div className="col-12 col-md-6 col-lg-3" data-rb-select="programType">
                                                        <RhyBusCustomSelect id="select-programType" name="Program Type" label="Program Type" placeholder="Select" isMandatory={true} info={resolveTooltip(TT_GRANT_PROGRAM_TYPE)} options={optionsMap['Program Type'] || []} value={values.programType} onChange={handleProgramTypeChange} />
                                                    </div>
                                                    <div className="col-12 col-md-6 col-lg-3" data-rb-select="fundingSource">
                                                        <RhyBusCustomSelect id="select-fundingSource" name="Funding Source" label="Funding Source" placeholder="Select" isMandatory={false} info={resolveTooltip(TT_GRANT_FUNDING_SOURCE)} options={optionsMap['Funding Source'] || []} value={values.fundingSource ?? null} onChange={(v: any) => handleChange('fundingSource', v)} isDisabled={true} />
                                                    </div>
                                                    <div className="col-12 col-md-6 col-lg-3" data-rb-select="implementingAgency">
                                                        <RhyBusCustomSelect id="select-ia" name="IA" label="IA" placeholder="Select" isMandatory={true} info={resolveTooltip(TT_GRANT_IA)} options={optionsMap['Implementing Agency'] || []} value={values.implementingAgency} onChange={(v: any) => handleChange('implementingAgency', v)} />
                                                    </div>
                                                    <div className="col-12 col-md-6 col-lg-3" data-rb-select="country">
                                                        <RhyBusCustomSelect id="select-country" name="Country" label="Country" placeholder="Select" isMandatory={false} info={resolveTooltip(TT_GRANT_COUNTRY)} options={optionsMap['Country'] || []} value={values.country} onChange={(v: any) => handleChange('country', v)} />
                                                    </div>
                                                    {/* <div id="cr-row-group-0-fields-2" className="row p-relative g-2 form-md darktext"> */}
                                                    <div className="col-12 col-md-6 col-lg-3" data-rb-select="fiscalYear">
                                                        <RhyBusCustomSelect id="select-fiscalYear" name="Start Fiscal year" label="Start Fiscal year" placeholder="Select" isMandatory={true} info={resolveTooltip(TT_GRANT_START_FY)} options={optionsMap['Fiscal Year'] || []} value={values.fiscalYear} onChange={(v: any) => handleChange('fiscalYear', v)} />
                                                    </div>
                                                    <div className="col-12 col-md-6 col-lg-3" data-rb-select="endFiscalYear">
                                                        <RhyBusCustomSelect id="select-endFiscalYear" name="End Fiscal Year" label="End Fiscal Year" placeholder="Select" isMandatory={false} info={resolveTooltip(TT_GRANT_END_FY)} options={endFiscalYearOptions || []} value={values.endFiscalYear ?? null} onChange={(v: any) => handleChange('endFiscalYear', v)} />
                                                    </div>
                                                    <div className="col-12 col-md-6 col-lg-3">
                                                        <RhyBusInputText key={`caseDesignator-${typeof values.fiscalYear === 'object' ? values.fiscalYear?.value : values.fiscalYear}-${typeof values.programType === 'object' ? values.programType?.value : values.programType}`} id="input-caseDesignator" name="Case Designator" label="Case Designator" placeholder="Auto-generated" isMandatory={false} info={resolveTooltip(TT_GRANT_CASE_DESIGNATOR)} value={values.caseDesignator || ''} readOnly={true} />
                                                    </div>
                                                    <div className="col-12 col-md-6 col-lg-3">

                                                        <RhyBusInputText id="input-userCaseId"
                                                            key={values.userCaseId} name="User Case ID" label="User Case ID" placeholder="Auto-generated" isMandatory={false} info={resolveTooltip(TT_GRANT_USER_CASE_ID)} value={values.userCaseId || ''} readOnly={true} />

                                                    </div>
                                                </div>
                                            </div>

                                            {/* Grant Lines Section */}
                                            <div id="grantLines" style={{ borderRadius: '10px' }}>
                                                {/* Header */}
                                                <div style={{ backgroundColor: '#434D8A', padding: '10px 16px', borderRadius: '4px 4px 0 0' }} tabIndex={0} aria-label='Case Lines'>
                                                    <span className="inter-semibold font-14" style={{ color: '#fff' }}>Case Lines</span>
                                                </div>
                                                {/* Input row */}
                                                <div className="p-3 pt-2" style={{ border: '1px solid #CED2D7', borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px' }}>
                                                    <div className="row align-items-end">
                                                        <div className="col-12 col-md">
                                                            <RhyBusInputText
                                                                key={grantLineResetKey}
                                                                id="input-grantLineNumber"
                                                                name="Line"
                                                                label="Line"
                                                                placeholder="Enter Line Number"
                                                                isMandatory={true}
                                                                error={grantLineInputError ? true : false}
                                                                errorMessage={grantLineInputError ? grantLineInputError : ''}
                                                                info={resolveTooltip(TT_GRANT_LINE_NUMBER)}
                                                                value={grantLineInput.lineNumber}
                                                                onFocus={() => {
                                                                    if (editingGrantLineIndex !== null) {
                                                                        setInlineEditError(null)
                                                                        setEditingGrantLineIndex(null)
                                                                        setInlineEditValues({ lineNumber: '', regionalCenter: '' })
                                                                    }
                                                                }}
                                                                onChange={(e: any) => {
                                                                    setGrantLineInput(prev => ({ ...prev, lineNumber: e?.target?.value ?? e ?? '' }))
                                                                    setGrantLineInputError(null)
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="col-12 col-md">
                                                            <RhyBusCustomSelect
                                                                id="select-regionalCenter"
                                                                name="Regional Center"
                                                                label="Regional Center"
                                                                placeholder="Select"
                                                                isMandatory={false}
                                                                info={resolveTooltip(TT_GRANT_REGIONAL_CENTER)}
                                                                options={regionalCenterOptions}
                                                                value={regionalCenterOptions.find(o => o.label === grantLineInput.regionalCenter) ?? null}
                                                                onFocus={() => {
                                                                    if (editingGrantLineIndex !== null) {
                                                                        setInlineEditError(null)
                                                                        setEditingGrantLineIndex(null)
                                                                        setInlineEditValues({ lineNumber: '', regionalCenter: '' })
                                                                    }
                                                                }}
                                                                onChange={(val: any) => setGrantLineInput(prev => ({ ...prev, regionalCenter: val?.label ?? val?.value ?? val ?? '' }))}
                                                            />
                                                        </div>
                                                        <div className="col-12 col-md-auto">
                                                            <RhyBusButton
                                                                variant="primary"
                                                                size='md'
                                                                label='Add'
                                                                leftIcon='icon-rb-add font-11'
                                                                onClick={handleGrantLineAdd}
                                                            />
                                                        </div>
                                                    </div>
                                                    {/* Table */}
                                                    <div className="mt-4" style={{ border: '1px solid #D5D4F1', borderRadius: '9px', overflow: 'hidden' }}>
                                                        <table role="grid" aria-label="Grant Lines" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                            <thead>
                                                                <tr style={{ backgroundColor: '#F2F1FF' }}>
                                                                    <th scope="col" tabIndex={0} aria-label="Line Number" style={{ padding: '10px 16px', fontWeight: 600, fontSize: '13px', borderBottom: '1px solid #D5D4F1', borderRight: '1px solid #D5D4F1' }}>Line Number</th>
                                                                    <th scope="col" tabIndex={0} aria-label="Regional Center" style={{ padding: '10px 16px', fontWeight: 600, fontSize: '13px', borderBottom: '1px solid #D5D4F1', borderRight: '1px solid #D5D4F1' }}>Regional Center</th>
                                                                    <th scope="col" tabIndex={0} aria-label="Actions" style={{ padding: '10px 16px', width: '90px', borderBottom: '1px solid #D5D4F1' }}></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody aria-live="polite">
                                                                {grantLines.length === 0 ? (
                                                                    <tr>
                                                                        <td colSpan={3} role="status" tabIndex={0} aria-label="No grant lines added" style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', color: '#6c757d' }}>No records found.</td>
                                                                    </tr>
                                                                ) : (
                                                                    grantLines.map((line, index) => (
                                                                        <tr
                                                                            key={index}
                                                                            tabIndex={editingGrantLineIndex === index ? -1 : 0}
                                                                            aria-label={`Grant line ${index + 1}: Line Number ${line.lineNumber}, Regional Center ${line.regionalCenter || 'Not Applicable'}${editingGrantLineIndex === index ? ', currently editing' : ''}`}
                                                                            style={{ borderBottom: index < grantLines.length - 1 ? '1px solid #D5D4F1' : 'none' }}
                                                                        >
                                                                            <td style={{ padding: '4px 12px', borderRight: '1px solid #D5D4F1' }}>
                                                                                {editingGrantLineIndex === index ? (
                                                                                    <>
                                                                                        <RhyBusInputText
                                                                                            id={`input-inlineLineNumber-${index}`}
                                                                                            name="Line"
                                                                                            placeholder="Enter Line Number"
                                                                                            value={inlineEditValues.lineNumber}
                                                                                            onChange={(e: any) => {
                                                                                                const val = e?.target?.value ?? e ?? ''
                                                                                                setInlineEditValues(prev => ({ ...prev, lineNumber: val }))
                                                                                                if (val.trim()) setInlineEditError(null)
                                                                                            }}
                                                                                        />
                                                                                        {inlineEditError && <div style={{ color: '#C83434', fontSize: '12px', marginTop: '2px' }}>{inlineEditError}</div>}
                                                                                    </>
                                                                                ) : (
                                                                                    <span style={{ display: 'block', textAlign: 'center', fontSize: '13px' }}>{line.lineNumber}</span>
                                                                                )}
                                                                            </td>
                                                                            <td style={{ padding: '4px 12px', borderRight: '1px solid #D5D4F1' }}>
                                                                                {editingGrantLineIndex === index ? (
                                                                                    <RhyBusCustomSelect
                                                                                        id={`select-inlineRegionalCenter-${index}`}
                                                                                        name="Regional Center"
                                                                                        placeholder="Select"
                                                                                        options={regionalCenterOptions}
                                                                                        value={regionalCenterOptions.find(o => o.label === inlineEditValues.regionalCenter) ?? null}
                                                                                        onChange={(val: any) => setInlineEditValues(prev => ({ ...prev, regionalCenter: String(val?.label ?? val?.value ?? val ?? '') }))}
                                                                                    />
                                                                                ) : (
                                                                                    <span style={{ display: 'block', textAlign: 'center', fontSize: '13px' }}>{line.regionalCenter || 'Not Applicable'}</span>
                                                                                )}
                                                                            </td>
                                                                            <td>
                                                                                <div className="d-flex align-items-center justify-content-center">
                                                                                    {editingGrantLineIndex === index ? (
                                                                                        <>
                                                                                            <RhyBusButton
                                                                                                variant="text"
                                                                                                ariaLabel={`Save changes to grant line ${index + 1}`}
                                                                                                leftIcon="icon-rb-checked font-13 color-primary"
                                                                                                onClick={() => handleGrantLineSave(index)}
                                                                                            />
                                                                                            <RhyBusButton
                                                                                                variant="ghost"
                                                                                                ariaLabel={`Cancel editing grant line ${index + 1}`}
                                                                                                leftIcon="icon-rb-close font-12"
                                                                                                onClick={handleGrantLineCancelEdit}
                                                                                                style={{ '--rb-btn-bg': '#C83434' }}
                                                                                            />
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            <RhyBusButton
                                                                                                variant="text"
                                                                                                ariaLabel={`Edit grant line ${index + 1}: ${line.lineNumber}`}
                                                                                                leftIcon="icon-rb-pencil font-12 color-primary"
                                                                                                onClick={() => handleGrantLineEdit(index)}
                                                                                            />
                                                                                            <RhyBusButton
                                                                                                variant="text"
                                                                                                ariaLabel={`Delete grant line ${index + 1}: ${line.lineNumber}`}
                                                                                                leftIcon="icon-rb-trashbin font-12"
                                                                                                onClick={() => handleGrantLineDelete(index)}
                                                                                            />
                                                                                        </>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>

                                            <div id="cr-row-group-0-fields-4" className="col-12 form-md">
                                                <RhyBusTextArea id="textarea-description" name="Description" label="Description" placeholder="Enter Description" isMandatory={false} info={resolveTooltip(TT_GRANT_DESCRIPTION)} rows={5} value={values.description || ''} onChange={(e: any) => handleChange('description', e?.target?.value ?? e)} />
                                            </div>
                                            <div id="cr-row-group-0-fields-5" className="col-12 form-md">
                                                <div onChangeCapture={(e: any) => captureActualFiles('attachments', e)}>
                                                    <RhyBusFileUpload
                                                        label="Attachments"
                                                        hint="Upload the file which are in the .png, .jpeg, .xlsx,.txt .docx, .pdf files. Maximum size for file is 25 MB."
                                                        note="Screenshots need to be saved locally before uploading into system."
                                                        mandatory={false}
                                                        info={resolveTooltip(TT_GRANT_ATTACHMENTS)}
                                                        files={values.attachments || []}
                                                        onChange={(files: any) => onFileChange('attachments', files)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        {validationMsgs && validationMsgs.length > 0 && position === 'bottom' && (
                                            <div id="validation-summary-bottom" className='mt-3'>
                                                <RhyBusValidationSummary
                                                    errors={validationMsgs.map((m: any) => ({ name: m.key, label: m.text }))}
                                                    variant="info"
                                                    showClose={true}
                                                    onClose={() => setValidationMsgs([])}
                                                    onErrorClick={(error: any) => focusField(error.name)}
                                                />
                                            </div>
                                        )}
                                    </>
                                }
                                cardFooter={
                                    <div className='d-flex justify-content-end w-100 gap-2 flex-wrap'>
                                        <RhyBusButton variant="primary" label="Cancel" leftIcon="icon-rb-close font-11 color-primary" onClick={handleCancelClick} style={{ '--rb-btn-text': '#030332', '--rb-btn-hover-bg': '#fff', '--rb-btn-bg': '#fff', '--rb-btn-border': '#C0CAD8', '--rb-btn-hover-border': '#C0CAD8', fontWeight: '600' }} />
                                        {buttonsReady && showSaveAsDraft && <RhyBusButton variant="primary" label={primaryActionLabel} leftIcon={primaryActionIcon} onClick={() => handleSaveAction('bottom')} style={{ '--rb-btn-text': '#030332', '--rb-btn-hover-bg': '#fff', '--rb-btn-bg': '#fff', '--rb-btn-border': '#C0CAD8', '--rb-btn-hover-border': '#C0CAD8', fontWeight: '600' }} />}
                                        {buttonsReady && <RhyBusButton variant="primary" label={submitActionLabel} leftIcon={submitActionIcon} onClick={() => handleSubmitAction('bottom')} style={{ fontWeight: '600' }} />}
                                    </div>
                                }
                            />
                        </div>
                    </div>
                </div>
            </main>
        </>
    )
}

export default Form
